// In-game dialogs: session summary, lobby rules (host settings), lobby password.
import { useMemo, useState, type FormEvent } from "react";
import {
  LOBBY_PASSWORD_MAX,
  LobbyPasswordSchema,
  LobbySettingsPatchSchema,
  MAX_ANTE,
  MAX_HAND_LIMIT,
  MAX_TURN_SECONDS,
  MIN_ANTE,
  MIN_TURN_SECONDS,
  type LobbySettings,
  type LobbySettingsPatch,
  type SessionSummary,
} from "@wp/shared";
import { settingsPatch, toDraft, type SettingsDraft } from "../../game/lobbySettings";
import { searchSpells, spellEntries } from "../../game/spellbook";
import { formatCoins } from "../../lib/format";
import { Modal } from "../common/Modal";

// ---------- session summary ----------

export function SessionSummaryDialog({
  summary,
  myUserId,
  onClose,
}: {
  summary: SessionSummary;
  myUserId: string | null;
  onClose: () => void;
}) {
  const players = [...summary.players].sort((a, b) => b.handsWon - a.handsWon || b.net - a.net);
  return (
    <Modal title="Session over" onClose={onClose} testId="session-summary">
      <p>
        {summary.handsPlayed} hand{summary.handsPlayed === 1 ? "" : "s"} played.
      </p>
      {summary.winners.length > 0 && (
        <ul className="summary-winners">
          {summary.winners.map((w) => (
            <li key={w.userId}>
              <strong>{w.userId === myUserId ? "You" : w.username}</strong> won the session with {w.handsWon} hand
              {w.handsWon === 1 ? "" : "s"}
            </li>
          ))}
        </ul>
      )}
      <table className="summary-table">
        <thead>
          <tr>
            <th scope="col">Player</th>
            <th scope="col">Hands won</th>
            <th scope="col">Net coins</th>
          </tr>
        </thead>
        <tbody>
          {players.map((p) => (
            <tr key={p.userId} className={p.userId === myUserId ? "me" : undefined}>
              <td>{p.username}</td>
              <td>{p.handsWon}</td>
              <td className={p.net > 0 ? "pos" : p.net < 0 ? "neg" : undefined}>
                {p.net > 0 ? "+" : ""}
                {formatCoins(p.net)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="dialog-actions">
        <button type="button" className="btn primary" onClick={onClose} data-testid="session-summary-close">
          Close
        </button>
      </div>
    </Modal>
  );
}

// ---------- lobby rules ----------

/** Validate a settings patch; a friendly message for the first bad field, or null. */
export function settingsProblem(patch: LobbySettingsPatch): { patch: LobbySettingsPatch | null; error: string | null } {
  const parsed = LobbySettingsPatchSchema.safeParse(patch);
  if (parsed.success) return { patch: parsed.data, error: null };
  const field = String(parsed.error.issues[0]?.path[0] ?? "");
  const hints: Record<string, string> = {
    ante: `Min must be ${MIN_ANTE}-${MAX_ANTE}.`,
    turnSeconds: `Turn timer must be ${MIN_TURN_SECONDS}-${MAX_TURN_SECONDS} seconds.`,
    handLimit: `Hand limit must be 1-${MAX_HAND_LIMIT}, or unlimited.`,
  };
  return { patch: null, error: hints[field] ?? "Some settings are invalid." };
}

/** The game-settings fields (ante, timers, hand limit, spectators, spell rules). */
export function GameSettingsFields({
  draft,
  set,
  lockGame,
  canEdit,
}: {
  draft: SettingsDraft;
  set: (p: Partial<SettingsDraft>) => void;
  lockGame: boolean;
  canEdit: boolean;
}) {
  const [spellQuery, setSpellQuery] = useState("");
  const spells = useMemo(() => searchSpells(spellEntries(), spellQuery), [spellQuery]);
  return (
    <>
      <div className="rules-grid">
        <label className="field">
          <span>Min / minimum entry (coins)</span>
          <input
            type="number"
            inputMode="numeric"
            min={MIN_ANTE}
            max={MAX_ANTE}
            value={draft.ante}
            disabled={lockGame}
            onChange={(e) => set({ ante: e.target.value })}
            data-testid="settings-ante"
          />
        </label>
        <label className="field">
          <span>Turn timer (s)</span>
          <input
            type="number"
            inputMode="numeric"
            min={MIN_TURN_SECONDS}
            max={MAX_TURN_SECONDS}
            value={draft.turnSeconds}
            disabled={lockGame}
            onChange={(e) => set({ turnSeconds: e.target.value })}
            data-testid="settings-turn"
          />
        </label>
        <div className="field">
          <span>Hands per session</span>
          <div className="inline-field">
            <label className="toggle-row compact">
              <input type="checkbox" checked={draft.unlimited} disabled={lockGame} onChange={(e) => set({ unlimited: e.target.checked })} />
              <span>Unlimited</span>
            </label>
            {!draft.unlimited && (
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_HAND_LIMIT}
                value={draft.handLimit}
                disabled={lockGame}
                onChange={(e) => set({ handLimit: e.target.value })}
                aria-label="Hand limit"
                data-testid="settings-hand-limit"
              />
            )}
          </div>
        </div>
      </div>
      <label className="toggle-row">
        <input type="checkbox" checked={draft.spectatorsAllowed} disabled={!canEdit} onChange={(e) => set({ spectatorsAllowed: e.target.checked })} />
        <span>Allow spectators</span>
      </label>
      <label className="toggle-row">
        <input
          type="checkbox"
          checked={draft.hideCardsFromSpectators}
          disabled={!canEdit}
          onChange={(e) => set({ hideCardsFromSpectators: e.target.checked })}
        />
        <span>Hide hole cards from spectators</span>
      </label>

      <fieldset className="spell-rules" disabled={lockGame}>
        <legend>Spell rules (ban spells at this table)</legend>
        <input
          type="search"
          className="spell-search"
          placeholder="Filter spells…"
          aria-label="Filter spells"
          value={spellQuery}
          onChange={(e) => setSpellQuery(e.target.value)}
        />
        <ul>
          {spells.map(({ def }) => {
            const value = draft.spellRules[def.id] === "banned" ? "banned" : "allowed";
            return (
              <li key={def.id}>
                <span>{def.name}</span>
                <select
                  value={value}
                  aria-label={`${def.name} rule`}
                  onChange={(e) => {
                    const next = { ...draft.spellRules };
                    const v = e.target.value;
                    if (v === "banned") next[def.id] = "banned";
                    else delete next[def.id];
                    set({ spellRules: next });
                  }}
                >
                  <option value="allowed">Allowed</option>
                  <option value="banned">Banned</option>
                </select>
              </li>
            );
          })}
        </ul>
      </fieldset>
    </>
  );
}

export function LobbyRulesDialog({
  settings,
  canEdit,
  playing,
  onSave,
  onClose,
  extra,
}: {
  settings: LobbySettings;
  canEdit: boolean;
  playing: boolean;
  onSave: (patch: LobbySettingsPatch) => boolean;
  onClose: () => void;
  /** Extra controls shown above the settings (lobby name / password / code). */
  extra?: React.ReactNode;
}) {
  const [draft, setDraft] = useState<SettingsDraft>(() => toDraft(settings));
  const [error, setError] = useState<string | null>(null);
  // While a session runs only the spectator flags may change.
  const lockGame = !canEdit || playing;
  const set = (p: Partial<SettingsDraft>) => setDraft((d) => ({ ...d, ...p }));

  function save(e: FormEvent) {
    e.preventDefault();
    const patch = settingsPatch(settings, draft);
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    const checked = settingsProblem(patch);
    if (!checked.patch) {
      setError(checked.error);
      return;
    }
    if (onSave(checked.patch)) onClose();
    else setError("Not connected. Try again in a moment.");
  }

  return (
    <Modal title={canEdit ? "Game settings" : "Game rules"} onClose={onClose} testId="lobby-settings" className="wide">
      <form className="stack-form rules-form" onSubmit={save} noValidate>
        {extra}
        {playing && canEdit && <p className="muted small">A session is running: only spectator options can change.</p>}
        <GameSettingsFields draft={draft} set={set} lockGame={lockGame} canEdit={canEdit} />
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="dialog-actions">
          <button type="button" className="btn" onClick={onClose}>
            {canEdit ? "Cancel" : "Close"}
          </button>
          {canEdit && (
            <button type="submit" className="btn primary" data-testid="settings-save">
              Save
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}

// ---------- private lobby password ----------

export function PasswordDialog({ onSave, onClose }: { onSave: (password: string) => boolean; onClose: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  function save(e: FormEvent) {
    e.preventDefault();
    if (!LobbyPasswordSchema.safeParse(password).success) {
      setError(`Passwords are 1-${LOBBY_PASSWORD_MAX} characters.`);
      return;
    }
    if (onSave(password)) onClose();
    else setError("Not connected. Try again in a moment.");
  }

  return (
    <Modal title="Change lobby password" onClose={onClose} testId="password-dialog">
      <form className="stack-form" onSubmit={save} noValidate>
        <label className="field">
          <span>New password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            maxLength={LOBBY_PASSWORD_MAX}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            data-testid="lobby-new-password"
          />
        </label>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="dialog-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary">
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}
